import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Search, Filter, MoreVertical, Calendar, User, Tag, CheckSquare,
  Clock, AlertCircle, ChevronDown, Trash2, Edit, MessageSquare, Paperclip,
  Folder, FolderPlus, X, GripVertical, CheckCircle2, Circle, ArrowRight,
  Flag, Users, Target, Shield, FileText, AlertTriangle, ClipboardCheck,
  LayoutGrid, List, RefreshCw, ChevronRight, Loader2, Send, Link, Image,
  File, Download, Copy, Sparkles, Check
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  todo: { label: 'To Do', color: 'bg-slate-100 border-slate-300', textColor: 'text-slate-700', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 border-blue-300', textColor: 'text-blue-700', icon: Clock },
  review: { label: 'Review', color: 'bg-purple-100 border-purple-300', textColor: 'text-purple-700', icon: Target },
  done: { label: 'Done', color: 'bg-green-100 border-green-300', textColor: 'text-green-700', icon: CheckCircle2 }
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600', icon: Flag },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-600', icon: Flag },
  high: { label: 'High', color: 'bg-orange-100 text-orange-600', icon: Flag },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-600', icon: AlertCircle }
};

// Category configuration
const CATEGORY_CONFIG = {
  general: { label: 'General', color: 'bg-slate-100 text-slate-700', icon: FileText },
  corrective_action: { label: 'Corrective Action', color: 'bg-orange-100 text-orange-700', icon: Shield },
  safety_improvement: { label: 'Safety Improvement', color: 'bg-green-100 text-green-700', icon: Target },
  incident_followup: { label: 'Incident Follow-up', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  audit_finding: { label: 'Audit Finding', color: 'bg-purple-100 text-purple-700', icon: ClipboardCheck }
};

const ProjectManagementPage = () => {
  const { toast } = useToast();
  const [board, setBoard] = useState({ todo: [], in_progress: [], review: [], done: [] });
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('board'); // board or list
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Dialogs
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showTaskDetail, setShowTaskDetail] = useState(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  
  // Comments
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  
  // Attachments
  const [showAttachmentDialog, setShowAttachmentDialog] = useState(false);
  const [newAttachment, setNewAttachment] = useState({ name: '', url: '', type: 'link' });
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    category: 'general',
    assignee_name: '',
    due_date: '',
    tags: [],
    checklist: []
  });
  
  // New project form
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });
  
  // Dragging state
  const [draggedTask, setDraggedTask] = useState(null);

  const fetchBoard = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const params = selectedProject ? `?project_id=${selectedProject}` : '';
      const response = await axios.get(`${API_URL}/api/projects/tasks/board${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBoard(response.data.board);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching board:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API_URL}/api/projects/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchBoard();
    fetchProjects();
    fetchTemplates();
  }, [fetchBoard]);

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast({ title: "Error", description: "Task title is required", variant: "destructive" });
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      const taskData = {
        ...newTask,
        project_id: selectedProject
      };
      
      await axios.post(`${API_URL}/api/projects/tasks`, taskData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Task created successfully" });
      setShowTaskDialog(false);
      setNewTask({
        title: '',
        description: '',
        status: 'todo',
        priority: 'medium',
        category: 'general',
        assignee_name: '',
        due_date: '',
        tags: [],
        checklist: []
      });
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create task", variant: "destructive" });
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/projects/tasks/${taskId}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
    }
  };

  const handleMoveTask = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(`${API_URL}/api/projects/tasks/${taskId}/move`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to move task", variant: "destructive" });
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/projects/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Task deleted" });
      setShowTaskDetail(null);
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete task", variant: "destructive" });
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast({ title: "Error", description: "Project name is required", variant: "destructive" });
      return;
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API_URL}/api/projects`, newProject, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Project created successfully" });
      setShowProjectDialog(false);
      setNewProject({ name: '', description: '', color: '#3B82F6' });
      fetchProjects();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create project", variant: "destructive" });
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Project deleted" });
      if (selectedProject === projectId) setSelectedProject(null);
      fetchProjects();
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete project", variant: "destructive" });
    }
  };

  // Create task from template
  const handleCreateFromTemplate = async (templateId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/projects/tasks/from-template/${templateId}`, 
        { project_id: selectedProject },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: "Success", description: "Task created from template" });
      setShowTemplateDialog(false);
      fetchBoard();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create task from template", variant: "destructive" });
    }
  };

  // Add comment to task
  const handleAddComment = async () => {
    if (!newComment.trim() || !showTaskDetail) return;
    
    setCommentLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/projects/tasks/${showTaskDetail.id}/comments`,
        { comment: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setShowTaskDetail({
        ...showTaskDetail,
        comments: [...(showTaskDetail.comments || []), response.data]
      });
      setNewComment('');
      toast({ title: "Success", description: "Comment added" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to add comment", variant: "destructive" });
    } finally {
      setCommentLoading(false);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    if (!showTaskDetail) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${API_URL}/api/projects/tasks/${showTaskDetail.id}/comments/${commentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowTaskDetail({
        ...showTaskDetail,
        comments: showTaskDetail.comments.filter(c => c.id !== commentId)
      });
      toast({ title: "Success", description: "Comment deleted" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete comment", variant: "destructive" });
    }
  };

  // Add attachment
  const handleAddAttachment = async () => {
    if (!newAttachment.name.trim() || !newAttachment.url.trim() || !showTaskDetail) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API_URL}/api/projects/tasks/${showTaskDetail.id}/attachments`,
        newAttachment,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowTaskDetail({
        ...showTaskDetail,
        attachments: [...(showTaskDetail.attachments || []), response.data]
      });
      setNewAttachment({ name: '', url: '', type: 'link' });
      setShowAttachmentDialog(false);
      toast({ title: "Success", description: "Attachment added" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to add attachment", variant: "destructive" });
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    if (!showTaskDetail) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(
        `${API_URL}/api/projects/tasks/${showTaskDetail.id}/attachments/${attachmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowTaskDetail({
        ...showTaskDetail,
        attachments: showTaskDetail.attachments.filter(a => a.id !== attachmentId)
      });
      toast({ title: "Success", description: "Attachment removed" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove attachment", variant: "destructive" });
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    if (draggedTask && draggedTask.status !== status) {
      handleMoveTask(draggedTask.id, status);
    }
    setDraggedTask(null);
  };

  // Filter tasks
  const filterTasks = (tasks) => {
    return tasks.filter(task => {
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (filterPriority !== 'all' && task.priority !== filterPriority) {
        return false;
      }
      if (filterCategory !== 'all' && task.category !== filterCategory) {
        return false;
      }
      return true;
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const isDueToday = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate).toDateString() === new Date().toDateString();
  };

  // Task Card Component
  const TaskCard = ({ task }) => {
    const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
    const categoryConfig = CATEGORY_CONFIG[task.category] || CATEGORY_CONFIG.general;
    const PriorityIcon = priorityConfig.icon;
    
    const checklistProgress = task.checklist?.length > 0
      ? Math.round((task.checklist.filter(i => i.completed).length / task.checklist.length) * 100)
      : null;

    return (
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, task)}
        onClick={() => setShowTaskDetail(task)}
        className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer hover:shadow-md transition-all group"
      >
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-medium text-slate-900 text-sm line-clamp-2 flex-1">{task.title}</h4>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        </div>
        
        {task.description && (
          <p className="text-xs text-slate-500 line-clamp-2 mb-2">{task.description}</p>
        )}
        
        <div className="flex flex-wrap gap-1 mb-2">
          <Badge className={`text-xs ${priorityConfig.color}`}>
            <PriorityIcon className="w-3 h-3 mr-1" />
            {priorityConfig.label}
          </Badge>
          <Badge className={`text-xs ${categoryConfig.color}`}>
            {categoryConfig.label}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            {task.due_date && (
              <span className={`flex items-center gap-1 ${
                isOverdue(task.due_date) ? 'text-red-600 font-medium' : 
                isDueToday(task.due_date) ? 'text-orange-600 font-medium' : ''
              }`}>
                <Calendar className="w-3 h-3" />
                {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
            {task.assignee_name && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {task.assignee_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {task.comments?.length > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="w-3 h-3" />
                {task.comments.length}
              </span>
            )}
            {checklistProgress !== null && (
              <span className="flex items-center gap-1">
                <CheckSquare className="w-3 h-3" />
                {checklistProgress}%
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Column Component
  const BoardColumn = ({ status, tasks }) => {
    const config = STATUS_CONFIG[status];
    const StatusIcon = config.icon;
    const filteredTasks = filterTasks(tasks);
    
    return (
      <div
        className={`flex-1 min-w-[280px] max-w-[320px] bg-slate-50 rounded-xl p-3`}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, status)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <StatusIcon className={`w-4 h-4 ${config.textColor}`} />
            <h3 className={`font-semibold ${config.textColor}`}>{config.label}</h3>
            <Badge variant="secondary" className="text-xs">{filteredTasks.length}</Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0"
            onClick={() => {
              setNewTask({ ...newTask, status });
              setShowTaskDialog(true);
            }}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="space-y-2 min-h-[200px]">
          {filteredTasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
          {filteredTasks.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No tasks
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6" data-testid="project-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Project Management</h1>
          <p className="text-slate-500">Manage tasks, safety actions, and corrective measures</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchBoard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowProjectDialog(true)} variant="outline">
            <FolderPlus className="w-4 h-4 mr-2" />
            New Project
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Sparkles className="w-4 h-4 mr-2" />
                From Template
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {templates.map((template) => (
                <DropdownMenuItem 
                  key={template.id}
                  onClick={() => handleCreateFromTemplate(template.id)}
                  className="flex flex-col items-start py-2"
                >
                  <span className="font-medium">{template.name}</span>
                  <span className="text-xs text-slate-500 line-clamp-1">{template.description}</span>
                </DropdownMenuItem>
              ))}
              {templates.length === 0 && (
                <div className="px-2 py-4 text-center text-sm text-slate-500">
                  No templates available
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setShowTaskDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Task
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-xs text-slate-500">Total Tasks</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-700">{stats.in_progress}</div>
              <div className="text-xs text-blue-600">In Progress</div>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-purple-700">{stats.review}</div>
              <div className="text-xs text-purple-600">In Review</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-700">{stats.done}</div>
              <div className="text-xs text-green-600">Completed</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-red-700">{stats.overdue}</div>
              <div className="text-xs text-red-600">Overdue</div>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-orange-700">{stats.due_today}</div>
              <div className="text-xs text-orange-600">Due Today</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedProject || 'all'} onValueChange={(v) => setSelectedProject(v === 'all' ? null : v)}>
          <SelectTrigger className="w-[180px]">
            <Folder className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All Projects" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Projects</SelectItem>
            {projects.map((project) => (
              <SelectItem key={project.id} value={project.id}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: project.color }} />
                  {project.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]">
            <Flag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]">
            <Tag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="flex items-center border rounded-lg overflow-hidden">
          <Button
            variant={viewMode === 'board' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('board')}
            className="rounded-none"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
            className="rounded-none"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {Object.entries(STATUS_CONFIG).map(([status]) => (
            <BoardColumn 
              key={status} 
              status={status} 
              tasks={board[status] || []} 
            />
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Task</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Priority</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Category</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Assignee</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Due Date</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(board).flat().filter(task => filterTasks([task]).length > 0).map((task) => {
                  const statusConfig = STATUS_CONFIG[task.status];
                  const priorityConfig = PRIORITY_CONFIG[task.priority];
                  const categoryConfig = CATEGORY_CONFIG[task.category];
                  
                  return (
                    <tr key={task.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setShowTaskDetail(task)}>
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{task.title}</div>
                        {task.description && <div className="text-xs text-slate-500 truncate max-w-xs">{task.description}</div>}
                      </td>
                      <td className="p-3">
                        <Badge className={`${statusConfig.color} ${statusConfig.textColor}`}>
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                      </td>
                      <td className="p-3">
                        <Badge className={categoryConfig.color}>{categoryConfig.label}</Badge>
                      </td>
                      <td className="p-3 text-sm text-slate-600">{task.assignee_name || '-'}</td>
                      <td className="p-3">
                        {task.due_date ? (
                          <span className={`text-sm ${
                            isOverdue(task.due_date) ? 'text-red-600 font-medium' : 
                            isDueToday(task.due_date) ? 'text-orange-600 font-medium' : 'text-slate-600'
                          }`}>
                            {new Date(task.due_date).toLocaleDateString()}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setShowTaskDetail(task); }}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600" 
                              onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
            <DialogDescription>Add a new task to your board</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Task title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Task description"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={newTask.status} onValueChange={(v) => setNewTask({ ...newTask, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={newTask.category} onValueChange={(v) => setNewTask({ ...newTask, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Assignee</Label>
              <Input
                value={newTask.assignee_name}
                onChange={(e) => setNewTask({ ...newTask, assignee_name: e.target.value })}
                placeholder="Assignee name"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>Organize your tasks into projects</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="Project name"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Project description"
              />
            </div>
            
            <div>
              <Label>Color</Label>
              <div className="flex gap-2 mt-2">
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'].map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${newProject.color === color ? 'border-slate-900' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewProject({ ...newProject, color })}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateProject}>Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Side Panel */}
      {showTaskDetail && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="task-detail-panel">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setShowTaskDetail(null)}
          />
          
          {/* Side Panel */}
          <div className="relative w-full max-w-xl bg-white h-full shadow-xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b bg-slate-50">
              <div className="flex items-center gap-2">
                <Badge className={STATUS_CONFIG[showTaskDetail.status]?.color}>
                  {STATUS_CONFIG[showTaskDetail.status]?.label}
                </Badge>
                <Badge className={PRIORITY_CONFIG[showTaskDetail.priority]?.color}>
                  {PRIORITY_CONFIG[showTaskDetail.priority]?.label}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowTaskDetail(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Content */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-6">
                {/* Title & Description */}
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{showTaskDetail.title}</h2>
                  {showTaskDetail.description && (
                    <p className="text-slate-600 mt-2">{showTaskDetail.description}</p>
                  )}
                </div>
                
                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <Badge className={`mt-1 ${CATEGORY_CONFIG[showTaskDetail.category]?.color}`}>
                      {CATEGORY_CONFIG[showTaskDetail.category]?.label}
                    </Badge>
                  </div>
                  {showTaskDetail.assignee_name && (
                    <div>
                      <Label className="text-xs text-slate-500">Assignee</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className="text-sm">{showTaskDetail.assignee_name}</span>
                      </div>
                    </div>
                  )}
                  {showTaskDetail.due_date && (
                    <div>
                      <Label className="text-xs text-slate-500">Due Date</Label>
                      <div className={`flex items-center gap-2 mt-1 text-sm ${
                        isOverdue(showTaskDetail.due_date) ? 'text-red-600 font-medium' : ''
                      }`}>
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(showTaskDetail.due_date).toLocaleDateString()}</span>
                        {isOverdue(showTaskDetail.due_date) && (
                          <Badge className="bg-red-100 text-red-600 text-xs">Overdue</Badge>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-slate-500">Created</Label>
                    <div className="text-sm text-slate-600 mt-1">
                      {new Date(showTaskDetail.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                {/* Quick Status Change */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Change Status</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                      const StatusIcon = config.icon;
                      return (
                        <Button
                          key={status}
                          variant={showTaskDetail.status === status ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => {
                            handleMoveTask(showTaskDetail.id, status);
                            setShowTaskDetail({ ...showTaskDetail, status });
                          }}
                        >
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Checklist */}
                {showTaskDetail.checklist?.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Checklist</Label>
                    <div className="mt-2 space-y-2">
                      {showTaskDetail.checklist.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center gap-2 p-2 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('auth_token');
                              await axios.put(
                                `${API_URL}/api/projects/tasks/${showTaskDetail.id}/checklist/${idx}`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              const updatedChecklist = [...showTaskDetail.checklist];
                              updatedChecklist[idx].completed = !updatedChecklist[idx].completed;
                              setShowTaskDetail({ ...showTaskDetail, checklist: updatedChecklist });
                            } catch (error) {
                              console.error('Error toggling checklist:', error);
                            }
                          }}
                        >
                          {item.completed ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-400" />
                          )}
                          <span className={item.completed ? 'line-through text-slate-400' : 'text-slate-700'}>
                            {item.text}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Progress 
                      value={Math.round((showTaskDetail.checklist.filter(i => i.completed).length / showTaskDetail.checklist.length) * 100)} 
                      className="mt-2 h-2"
                    />
                  </div>
                )}
                
                {/* Attachments Section */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Attachments ({showTaskDetail.attachments?.length || 0})
                    </Label>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAttachmentDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {showTaskDetail.attachments?.length > 0 ? (
                    <div className="mt-2 space-y-2">
                      {showTaskDetail.attachments.map((attachment) => (
                        <div 
                          key={attachment.id} 
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border group"
                        >
                          <div className="flex items-center gap-3">
                            {attachment.type === 'image' ? (
                              <Image className="w-5 h-5 text-green-600" />
                            ) : attachment.type === 'link' ? (
                              <Link className="w-5 h-5 text-blue-600" />
                            ) : (
                              <File className="w-5 h-5 text-slate-600" />
                            )}
                            <div>
                              <a 
                                href={attachment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-blue-600 hover:underline"
                              >
                                {attachment.name}
                              </a>
                              <div className="text-xs text-slate-400">
                                {new Date(attachment.uploaded_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => window.open(attachment.url, '_blank')}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 p-4 text-center text-sm text-slate-400 bg-slate-50 rounded-lg border border-dashed">
                      No attachments yet
                    </div>
                  )}
                </div>
                
                {/* Comments Section */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Comments ({showTaskDetail.comments?.length || 0})
                  </Label>
                  
                  {/* Comment Input */}
                  <div className="mt-2 flex gap-2">
                    <Input
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleAddComment} 
                      disabled={!newComment.trim() || commentLoading}
                      size="sm"
                    >
                      {commentLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {/* Comments List */}
                  {showTaskDetail.comments?.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {showTaskDetail.comments.map((comment) => (
                        <div key={comment.id} className="p-3 bg-slate-50 rounded-lg group">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center">
                                <User className="w-3 h-3 text-blue-600" />
                              </div>
                              <div>
                                <span className="text-sm font-medium text-slate-700">
                                  {comment.user_name || 'Unknown'}
                                </span>
                                <span className="text-xs text-slate-400 ml-2">
                                  {new Date(comment.created_at).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="opacity-0 group-hover:opacity-100 text-red-600"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-slate-600 mt-2 ml-9">{comment.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 p-4 text-center text-sm text-slate-400 bg-slate-50 rounded-lg border border-dashed">
                      No comments yet
                    </div>
                  )}
                </div>
                
                {/* Activity Log */}
                {showTaskDetail.activity_log?.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Activity</Label>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {showTaskDetail.activity_log.slice(-10).reverse().map((activity, idx) => (
                        <div key={idx} className="text-sm text-slate-600 flex items-start gap-2 p-2 bg-slate-50 rounded">
                          <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                          <div>
                            <span>
                              {activity.action === 'created' && 'Task created'}
                              {activity.action === 'created_from_template' && `Created from template: ${activity.template}`}
                              {activity.action === 'moved' && `Moved from ${STATUS_CONFIG[activity.from_status]?.label} to ${STATUS_CONFIG[activity.to_status]?.label}`}
                              {activity.action === 'updated' && `Updated: ${activity.changes?.join(', ')}`}
                            </span>
                            <div className="text-xs text-slate-400">
                              {new Date(activity.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {/* Footer Actions */}
            <div className="p-4 border-t bg-slate-50 flex justify-between">
              <Button 
                variant="outline" 
                className="text-red-600 hover:bg-red-50" 
                onClick={() => handleDeleteTask(showTaskDetail.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Task
              </Button>
              <Button variant="outline" onClick={() => setShowTaskDetail(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Attachment Dialog */}
      <Dialog open={showAttachmentDialog} onOpenChange={setShowAttachmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Attachment</DialogTitle>
            <DialogDescription>Add a link or file reference to this task</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newAttachment.name}
                onChange={(e) => setNewAttachment({ ...newAttachment, name: e.target.value })}
                placeholder="Attachment name"
              />
            </div>
            
            <div>
              <Label>URL *</Label>
              <Input
                value={newAttachment.url}
                onChange={(e) => setNewAttachment({ ...newAttachment, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            
            <div>
              <Label>Type</Label>
              <Select 
                value={newAttachment.type} 
                onValueChange={(v) => setNewAttachment({ ...newAttachment, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="link">
                    <div className="flex items-center gap-2">
                      <Link className="w-4 h-4" /> Link
                    </div>
                  </SelectItem>
                  <SelectItem value="file">
                    <div className="flex items-center gap-2">
                      <File className="w-4 h-4" /> File
                    </div>
                  </SelectItem>
                  <SelectItem value="image">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4" /> Image
                    </div>
                  </SelectItem>
                  <SelectItem value="document">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Document
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttachmentDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddAttachment}
              disabled={!newAttachment.name.trim() || !newAttachment.url.trim()}
            >
              Add Attachment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectManagementPage;
